import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import * as L from 'leaflet';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormArray,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { PortRequestFormServiceService } from 'src/app/shared/service/port-request-FormService/port-request-form.service.service';
import { VoyageRequestsService } from 'src/app/shared/service/voyage-requests/voyage-requests.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { VesselOnboardEmailComponent } from 'src/app/shared/components/vessel-onboard-email/vessel-onboard-email.component';
import { ToastrService } from 'ngx-toastr';
import { OverlapService } from 'src/app/shared/service/overlapService/overlap.service';
import { MatAutocompleteSelectedEvent, MAT_AUTOCOMPLETE_DEFAULT_OPTIONS } from '@angular/material/autocomplete';

@Component({
  selector: 'app-voyagerequest-form',
  templateUrl: './voyagerequest-form.component.html',
  styleUrls: ['./voyagerequest-form.component.scss'],
  host: { class: 'voyage-request-form-page' },
  providers: [
    {
      provide: MAT_AUTOCOMPLETE_DEFAULT_OPTIONS,
      useValue: { panelClass: 'voyage-dropdown-panel' },
    },
  ],
})
export class VoyagerequestFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  isConsumptionOn = true;
  voyageForm!: FormGroup;
  uploadedFixedEtaFile: File | null = null;
  lookerVessels: Array<{ vessel_name?: string; imo?: string | number }> = [];
  vesselSuggestions: Array<{ vessel_name?: string; imo?: string | number }> = [];

  // Autocomplete suggestion lists for Start, End, and Waypoints
  startPortSuggestions: any[] = [];
  endPortSuggestions: any[] = [];
  midPortSuggestions: { [index: number]: any[] } = {};
  filteredPortNames: any[] = [];

  readonly optimizationTypeOptions = [
    'Instructed Speed',
    'Fixed ETA',
    'Optimized Speed',
  ];
  showAddInput = false;
  newPortControl = new FormControl('');
  onboardedVessels = new Set<string>();
  isLoadingVesselDetails = false;
  vesselTypeAutoPopulated = false;
  formSubmitted = false;
  movedUpIndex: number | null = null;
  movedDownIndex: number | null = null;
  panelSpeedExpanded = false;
  panelFinancialsExpanded = false;
  panelSafetyExpanded = false;
  panelWeatherExpanded = false;

  // Others custom mode states
  vesselIsOthers = false;
  startIsOthers = false;
  endIsOthers = false;

  lastPortAutoPopulated = false;
  lastPortDetails: any = null;
  private lastQuickOnboardingImo: string | null = null;

  // Edit Mode & Lifecycle
  isEditMode = false;
  editingRequestId: string | null = null;
  editingItemStatus: string | null = null;
  canEditForm = true;
  merakiEditRemarks = '';

  operationOptions = [
    'Loading',
    'Discharging',
    'Others',
    'Owners Bunkering'
  ];

  priorityOptions = [
    'Normal',
    'High',
    'Low',
    'Critical'
  ];

  /** Fixed ETA block is Meraki-internal only (Meraki - Operations login). */
  get isMerakiLogin(): boolean {
    return localStorage.getItem('username') === 'Meraki - Operations';
  }

  get currentUser(): string {
    return localStorage.getItem('username') || '';
  }

  get requestsPageTitle(): string {
    return this.isMerakiLogin ? "Client Requests" : "My Requests";
  }

  get formPageHeading(): string {
    if (this.isEditMode) {
      return `Edit Routing Request ${this.editingRequestId || ''}`;
    }
    return 'Routing Request Form';
  }

  get minDepartureDate(): string {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  }

  get maxDepartureDate(): string {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split('T')[0];
  }

  get minDepartureDateObj(): Date {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  get maxDepartureDateObj(): Date {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  get summaryRouteDeparture(): string {
    if (this.startIsOthers && this.voyageForm.get('startCustomPortName')?.value) {
      return this.voyageForm.get('startCustomPortName')?.value;
    }
    const val = this.voyageForm.get('startPort')?.value;
    return (typeof val === 'string' ? val : val?.port_name) || 'Departure Port';
  }

  get summaryRouteDestination(): string {
    if (this.endIsOthers && this.voyageForm.get('endCustomPortName')?.value) {
      return this.voyageForm.get('endCustomPortName')?.value;
    }
    const val = this.voyageForm.get('endPort')?.value;
    return (typeof val === 'string' ? val : val?.port_name) || 'Destination Port';
  }

  get activeBunkersCount(): number {
    let count = 0;
    ['hsfoCheck', 'lsmgoCheck', 'vlsfoCheck', 'biofuelCheck'].forEach(k => {
      if (this.voyageForm.get(k)?.value) count++;
    });
    return count;
  }

  get activeAvoidancesList(): string[] {
    const list: string[] = [];
    if (this.voyageForm.get('avoidIce')?.value) list.push('Ice');
    if (this.voyageForm.get('avoidHRA')?.value) list.push('HRA');
    if (this.voyageForm.get('secaFuel')?.value) list.push('SECA/ECA');
    if (this.voyageForm.get('tct')?.value) list.push('TCT');
    return list;
  }

  constructor(
    private fb: FormBuilder,
    private requestFormService: RequestFormService,
    private portRequestFormService: PortRequestFormServiceService,
    private voyageRequestsService: VoyageRequestsService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private overlapService: OverlapService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.initForm();
  }

  private initForm(): void {
    this.voyageForm = this.fb.group({
      consumptionToggle: [true],

      /* 🔴 VESSEL (MANDATORY) */
      vesselName: ['', Validators.required],
      vesselType: ['', Validators.required],
      vesselEmail: ['', [Validators.required, Validators.email]],
      vesselIsOthers: [false],
      customVesselName: [''],

      imo: [''],
      yearBuilt: [''],

      /* 🔴 VOYAGE DETAILS */
      voyageRef: [''],
      condition: ['Laden', Validators.required],
      departureDate: ['', [Validators.required, this.departureDateRangeValidator()]],
      departureTime: ['', Validators.required],

      /* 🔴 ROUTING PREFERENCES */
      avoidIce: [true],
      avoidHRA: [false],
      secaFuel: [false],
      tct: [false],
      others: [false],
      routingRemarks: [''],

      /* 🔴 FINANCIAL */
      dailyRate: ['', Validators.required],
      etsPrice: [''],

      /* 🔴 ROUTE ENDPOINTS & STOPS */
      startPort: [''],
      startIsOthers: [false],
      startCustomPortName: [''],
      startUnlocode: [''],
      startLat: [''],
      startLatDir: ['N'],
      startLong: [''],
      startLongDir: ['E'],

      endPort: [''],
      endIsOthers: [false],
      endCustomPortName: [''],
      endUnlocode: [''],
      endLat: [''],
      endLatDir: ['N'],
      endLong: [''],
      endLongDir: ['E'],

      /* 🔴 ITINERARY (WAYPOINTS) */
      itinerary: this.fb.array([]),

      /* 🔴 OPTIMIZATION */
      optimizationType: ['Instructed Speed'],
      speed: [10],
      consumption: [''],
      speedRemarks: [''],
      fixedEtaEtdDate: [''],
      fixedEtaEtdHours: [''],
      fixedEtaEtdMinutes: [''],
      fixedEtaEtaDate: [''],
      fixedEtaEtaHours: [''],
      fixedEtaEtaMinutes: [''],
      fixedEtaMinSpeed: [''],
      fixedEtaMaxSpeed: [''],
      fixedEtaRegularSpeedChangeInterval: ['24.0'],
      fixedEtaClimoSpeedChangeInterval: ['72.0'],
      fixedEtaForecastHorizonH: ['240.0'],
      fixedEtaRoughWaveH: ['4.0'],
      fixedEtaRoughWindSpd: ['28.0'],
      fixedEtaModerateWaveH: ['2.0'],
      fixedEtaModerateWind: ['14.0'],

      /* 🔴 SAFETY MARGINS */
      underKeel: [1],
      airDraft: [2],
      aftDraft: [''],
      forwardDraft: [''],
      beta: [''],
      hazardDistance: [20],
      weatherRestriction: [true],

      /* 🔴 WEATHER SAFETY */
      maxWind: [40],
      maxWave: [6],
      distanceFromStop: [30],

      /* 🔴 SPEED SETS */
      speedSets: this.fb.array([this.createSpeedSet()]),

      /* 🔴 BUNKER PRICES */
      hsfoCheck: [false],
      hsfo: [{ value: '', disabled: true }],
      lsmgoCheck: [false],
      lsmgo: [{ value: '', disabled: true }],
      vlsfoCheck: [false],
      vlsfo: [{ value: '', disabled: true }],
      biofuelCheck: [false],
      biofuel: [{ value: '', disabled: true }],
    }, { validators: [this.validateRouteEndpoints] });
  }

  preventNonNumeric(event: KeyboardEvent): void {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  get minItineraryDateObj(): Date {
    const dep = this.voyageForm?.get('departureDate')?.value;
    if (dep instanceof Date) {
      return dep;
    }
    if (typeof dep === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dep)) {
      const parts = dep.split('T')[0].split('-');
      if (parts.length === 3) {
        return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }
    return this.minDepartureDateObj;
  }

  departureDateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;
      const dateVal = new Date(control.value);
      if (isNaN(dateVal.getTime())) return null;

      const selected = dateVal.setHours(0, 0, 0, 0);
      const today = new Date().setHours(0, 0, 0, 0);
      const diffDays = Math.round((selected - today) / (1000 * 60 * 60 * 24));
      if (diffDays < -7 || diffDays > 7) {
        return { dateOutOfRange: { min: this.minDepartureDate, max: this.maxDepartureDate, actualDays: diffDays } };
      }
      return null;
    };
  }

  isLoadingRequest = false;

  setTimePreset(time: string): void {
    this.voyageForm.patchValue({ departureTime: time });
  }

  clearItineraryDates(): void {
    if (!this.itinerary || this.itinerary.length === 0) return;
    this.itinerary.controls.forEach((control) => {
      control.patchValue({
        eta: '',
        etaTime: '',
        cancelDate: ''
      }, { emitEvent: false });
    });
    this.itinerary.updateValueAndValidity();
  }

  ngOnInit(): void {
    document.body.classList.add('routing-request-form-active');
    this.loadLookerVessels();
    this.loadPortDetails();

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const editId = params.get('editId') || params.get('requestId');
      if (editId) {
        this.loadRequestForEdit(editId);
      }
    });

    // Clear waypoint ETA and cancellation dates whenever departure date or time changes
    this.voyageForm.get('departureDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isLoadingRequest) {
          this.clearItineraryDates();
        }
      });

    this.voyageForm.get('departureTime')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isLoadingRequest) {
          this.clearItineraryDates();
        }
      });

    // Reactive Mean Draft Calculation: (aftDraft + forwardDraft) / 2
    const calcMeanDraft = () => {
      const aft = parseFloat(this.voyageForm.get('aftDraft')?.value);
      const fwd = parseFloat(this.voyageForm.get('forwardDraft')?.value);
      if (!isNaN(aft) && !isNaN(fwd)) {
        const mean = ((aft + fwd) / 2).toFixed(2);
        this.voyageForm.patchValue({ beta: parseFloat(mean) }, { emitEvent: false });
      }
    };

    this.voyageForm.get('aftDraft')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => calcMeanDraft());

    this.voyageForm.get('forwardDraft')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => calcMeanDraft());

    this.voyageForm.get('vesselName')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        if (this.vesselIsOthers) return;
        const match = this.findVesselByName(name);
        if (match?.imo != null && match.imo !== '') {
          this.applyVesselImoAndFetchDetails(String(match.imo));
        } else if (!name?.trim()) {
          if (this.voyageForm.get('imo')?.value || this.voyageForm.get('vesselType')?.value) {
            this.resetVesselAutoFields();
          }
        }
      });

    // Synchronize bunker price enable/disable based on checkboxes
    ['hsfo', 'lsmgo', 'vlsfo', 'biofuel'].forEach((fuel) => {
      this.voyageForm.get(`${fuel}Check`)?.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((checked) => {
          const control = this.voyageForm.get(fuel);
          if (checked) {
            control?.enable();
          } else {
            control?.disable();
            control?.setValue('');
          }
        });
    });

    this.voyageForm.get('consumptionToggle')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        this.isConsumptionOn = !!val;
      });
  }

  private loadRequestForEdit(requestId: string): void {
    this.isEditMode = true;
    this.editingRequestId = requestId;
    this.voyageRequestsService.getRequestById(requestId).subscribe({
      next: (res) => {
        if (res?.success && res.data) {
          const item = res.data;
          const isMeraki = this.isMerakiLogin;
          
          // Tenant protection: ensure client only accesses their own request
          if (!isMeraki && item.clientName && this.currentUser) {
            const clientLower = item.clientName.toLowerCase().trim();
            const userLower = this.currentUser.toLowerCase().trim();
            if (clientLower !== userLower && !clientLower.includes(userLower) && !userLower.includes(clientLower)) {
              this.toastr.error('You do not have permission to view or edit this routing request.', 'Access Denied');
              this.router.navigate(['/routing-request-list']);
              return;
            }
          }

          this.editingItemStatus = item.status;
          if (item.routeCsvName || item.rawPayload?.routeCsvName) {
            this.uploadedCsvFileName = item.routeCsvName || item.rawPayload.routeCsvName;
            this.uploadedCsvFileSize = item.routeCsvSize || item.rawPayload?.routeCsvSize || '';
            this.uploadedCsvData = item.routeCsvData || item.rawPayload?.routeCsvData || '';
            this.uploadedCsvRowCount = item.routeCsvRowCount || item.rawPayload?.routeCsvRowCount || 0;
          }
          this.patchFormWithRequestData(item);

          const isPermittedClient = item.status === 'Draft' || item.status === 'Edit Requested';

          if (!isMeraki && !isPermittedClient) {
            this.canEditForm = false;
            this.voyageForm.disable();
            this.toastr.info('This request is being processed by Meraki Operations and is displayed in read-only mode.', 'Read Only');
          }
        }
      },
      error: (err) => {
        const errorMsg = err?.error?.message || 'You do not have permission to view or edit this routing request.';
        this.toastr.error(errorMsg, 'Access Denied');
        this.router.navigate(['/routing-request-list']);
      }
    });
  }

  private patchFormWithRequestData(data: any): void {
    const raw = data.rawPayload || data;

    if (raw.vesselIsOthers) {
      this.vesselIsOthers = true;
    }
    if (raw.startIsOthers) {
      this.startIsOthers = true;
    }
    if (raw.endIsOthers) {
      this.endIsOthers = true;
    }

    let depDateVal = raw.departureDate || data.departureDate || '';
    if (depDateVal) {
      depDateVal = new Date(depDateVal);
    }

    this.voyageForm.patchValue({
      vesselName: raw.vesselName || data.vesselName || '',
      vesselType: raw.vesselType || data.vesselType || '',
      vesselEmail: raw.vesselEmail || data.vesselEmail || '',
      vesselIsOthers: !!raw.vesselIsOthers,
      customVesselName: raw.customVesselName || '',
      imo: raw.imo || data.imo || '',
      yearBuilt: raw.yearBuilt || data.yearBuilt || '',

      voyageRef: raw.voyageRef || data.voyageRef || '',
      condition: raw.condition || data.condition || 'Laden',
      departureDate: depDateVal,
      departureTime: raw.departureTime || data.departureTime || '',

      avoidIce: raw.avoidIce ?? true,
      avoidHRA: raw.avoidHRA ?? false,
      secaFuel: raw.secaFuel ?? false,
      tct: raw.tct ?? false,
      routingRemarks: raw.routingRemarks || data.routingRemarks || '',

      dailyRate: raw.dailyRate || data.dailyRate || '',
      etsPrice: raw.etsPrice || data.etsPrice || '',

      startPort: raw.startPort || data.startPort || '',
      startIsOthers: !!(raw.startIsOthers ?? data.startIsOthers),
      startCustomPortName: raw.startCustomPortName || data.startCustomPortName || '',
      startUnlocode: raw.startUnlocode || data.startUnlocode || '',
      startLat: (raw.startLat != null && raw.startLat !== '') ? Math.abs(raw.startLat) : ((data.startLat != null && data.startLat !== '') ? Math.abs(data.startLat) : null),
      startLatDir: this.resolveLatDir(raw.startLat ?? data.startLat, raw.startLatDir || data.startLatDir),
      startLong: (raw.startLong != null && raw.startLong !== '') ? Math.abs(raw.startLong) : ((data.startLong != null && data.startLong !== '') ? Math.abs(data.startLong) : null),
      startLongDir: this.resolveLongDir(raw.startLong ?? data.startLong, raw.startLongDir || data.startLongDir),

      endPort: raw.endPort || data.endPort || '',
      endIsOthers: !!(raw.endIsOthers ?? data.endIsOthers),
      endCustomPortName: raw.endCustomPortName || data.endCustomPortName || '',
      endUnlocode: raw.endUnlocode || data.endUnlocode || '',
      endLat: (raw.endLat != null && raw.endLat !== '') ? Math.abs(raw.endLat) : ((data.endLat != null && data.endLat !== '') ? Math.abs(data.endLat) : null),
      endLatDir: this.resolveLatDir(raw.endLat ?? data.endLat, raw.endLatDir || data.endLatDir),
      endLong: (raw.endLong != null && raw.endLong !== '') ? Math.abs(raw.endLong) : ((data.endLong != null && data.endLong !== '') ? Math.abs(data.endLong) : null),
      endLongDir: this.resolveLongDir(raw.endLong ?? data.endLong, raw.endLongDir || data.endLongDir),

      optimizationType: raw.optimizationType || data.optimizationType || 'Instructed Speed',
      speed: raw.speed || data.speed || 10,
      consumption: raw.consumption ?? data.consumption ?? '',
      speedRemarks: raw.speedRemarks || '',

      underKeel: raw.underKeel ?? 1,
      airDraft: raw.airDraft ?? 2,
      aftDraft: raw.aftDraft || '',
      forwardDraft: raw.forwardDraft || '',
      beta: raw.beta ?? data.beta ?? '',
      hazardDistance: raw.hazardDistance ?? data.hazardDistance ?? 20,
      weatherRestriction: raw.weatherRestriction ?? true,
      maxWind: raw.maxWind ?? 40,
      maxWave: raw.maxWave ?? 6,
      distanceFromStop: raw.distanceFromStop ?? data.distanceFromStop ?? 30,
    });

    // Populate bunkers
    ['hsfo', 'lsmgo', 'vlsfo', 'biofuel'].forEach((fuel) => {
      const price = raw[fuel] ?? data[fuel];
      if (price != null && price !== '') {
        this.voyageForm.get(`${fuel}Check`)?.setValue(true);
        this.voyageForm.get(fuel)?.enable();
        this.voyageForm.get(fuel)?.setValue(price);
      }
    });

    // Populate itinerary waypoints
    const itineraryData = raw.itinerary || data.itinerary;
    if (Array.isArray(itineraryData) && itineraryData.length > 0) {
      this.itinerary.clear();
      itineraryData.forEach((stop: any) => {
        this.itinerary.push(this.createItinerary(stop));
      });
    }

    // Populate speed sets
    const speedSetsData = raw.speedSets || data.speedSets;
    if (Array.isArray(speedSetsData) && speedSetsData.length > 0) {
      this.speedSets.clear();
      speedSetsData.forEach((set: any) => {
        this.speedSets.push(this.createSpeedSet(set));
      });
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('routing-request-form-active');
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isFixedEtaActive(): boolean {
    return this.voyageForm.get('optimizationType')?.value === 'Fixed ETA';
  }

  isLoadingVessels = false;

  private getLookerUsername(): string {
    return localStorage.getItem('username') || 'Meraki - Operations';
  }

  private deduplicateVessels(
    vessels: Array<{ vessel_name?: string; imo?: string | number; imo_number?: string | number }>
  ): Array<{ vessel_name: string; imo: string }> {
    const seen = new Set<string>();
    const result: Array<{ vessel_name: string; imo: string }> = [];

    for (const v of vessels) {
      const name = (v.vessel_name || '').trim();
      const rawImo = v.imo != null && v.imo !== '' ? v.imo : v.imo_number;
      const imo = String(rawImo || '').replace(/\D/g, '').trim();

      if (!name) continue;

      const key = imo ? `imo_${imo}` : `name_${name.toUpperCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({
          vessel_name: name,
          imo: imo
        });
      }
    }

    return result.sort((a, b) => a.vessel_name.localeCompare(b.vessel_name));
  }

  private isGraceUser(): boolean {
    const subuser = (localStorage.getItem('loginPic') ?? localStorage.getItem('subusername') ?? '').trim().toLowerCase();
    const user = (localStorage.getItem('username') ?? '').trim().toLowerCase();
    return subuser === 'grace' || user === 'emzoil_grace' || user.includes('grace');
  }

  private filterGraceVessels(
    vessels: Array<{ vessel_name?: string; imo?: string | number; imo_number?: string | number }>
  ): Array<any> {
    if (!this.isGraceUser()) return vessels;
    const graceImos = ['9378876', '9399480', '9438406', '9432048'];
    return vessels.filter((v: any) => {
      const rawImo = v.imo != null && v.imo !== '' ? v.imo : v.imo_number;
      const imo = String(rawImo || '').replace(/\D/g, '').trim();
      return graceImos.includes(imo);
    });
  }

  private getVesselCacheKey(): string {
    const user = this.getLookerUsername();
    const subuser = (localStorage.getItem('loginPic') ?? localStorage.getItem('subusername') ?? '').trim().toLowerCase();
    return `meraki_cached_vessels_${user.trim().toLowerCase().replace(/\s+/g, '_')}${subuser ? '_' + subuser : ''}`;
  }

  loadLookerVessels(): void {
    // Clean up legacy unscoped cache if present
    try {
      localStorage.removeItem('meraki_cached_vessels');
    } catch (e) { }

    const cacheKey = this.getVesselCacheKey();
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        let parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (this.isGraceUser()) {
            parsed = this.filterGraceVessels(parsed);
          }
          this.lookerVessels = this.deduplicateVessels(parsed);
          this.vesselSuggestions = this.lookerVessels.slice(0, 30);
          this.lookerVessels.forEach((v) => {
            if (v.vessel_name) this.onboardedVessels.add(v.vessel_name.trim().toUpperCase());
          });
        }
      }
    } catch (e) {
      console.warn('Could not read cached vessels:', e);
    }

    this.isLoadingVessels = true;
    const collectedVessels: Array<any> = [...this.lookerVessels];
    const username = this.getLookerUsername();

    this.portRequestFormService
      .getLookerStudioVessels(username)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vessels) => {
          if (Array.isArray(vessels)) {
            collectedVessels.push(...vessels);
          }
          this.finalizeVesselList(collectedVessels);
        },
        error: () => {
          this.finalizeVesselList(collectedVessels);
        },
      });

    // Only load global Q88 vessel list for Meraki Operations internal team
    if (this.isMerakiLogin) {
      this.requestFormService
        .getVesselList()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const q88List = res?.data ?? res;
            if (Array.isArray(q88List)) {
              collectedVessels.push(...q88List);
            }
            this.finalizeVesselList(collectedVessels);
          },
          error: () => {
            this.finalizeVesselList(collectedVessels);
          }
        });
    }
  }

  private finalizeVesselList(rawList: Array<any>): void {
    if (this.isGraceUser()) {
      rawList = this.filterGraceVessels(rawList);
    }
    const unique = this.deduplicateVessels(rawList);
    if (unique.length > 0) {
      this.lookerVessels = unique;
      unique.forEach((v) => {
        if (v.vessel_name) {
          this.onboardedVessels.add(v.vessel_name.trim().toUpperCase());
        }
      });

      const currentInput = (this.voyageForm.get('vesselName')?.value || '').trim().toLowerCase();
      if (currentInput) {
        this.vesselSuggestions = this.lookerVessels
          .filter((v) =>
            (v.vessel_name || '').toLowerCase().includes(currentInput) ||
            String(v.imo || '').includes(currentInput)
          )
          .slice(0, 30);
      } else {
        this.vesselSuggestions = this.lookerVessels.slice(0, 30);
      }

      try {
        localStorage.setItem(this.getVesselCacheKey(), JSON.stringify(this.lookerVessels));
      } catch (e) { }
    }
    this.isLoadingVessels = false;
  }

  loadPortDetails(): void {
    this.requestFormService
      .getPortDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.filteredPortNames = response?.data ?? [];
        },
        error: () => {
          this.toastr.error('Failed to load port list');
          this.filteredPortNames = [];
        },
      });
  }

  filterPortNames(event: Event, type: 'start' | 'end'): void {
    const query = (event.target as HTMLInputElement).value?.trim().toLowerCase();
    const suggestions = !query
      ? this.filteredPortNames.slice(0, 20)
      : this.filteredPortNames
        .filter(
          (p) =>
            (p.port_name || '').toLowerCase().includes(query) ||
            (p.country || '').toLowerCase().includes(query),
        )
        .slice(0, 20);

    if (type === 'start') {
      this.startPortSuggestions = suggestions;
    } else {
      this.endPortSuggestions = suggestions;
    }
  }

  filterMidPortNames(event: Event, index: number): void {
    const query = (event.target as HTMLInputElement).value?.trim().toLowerCase();
    this.midPortSuggestions[index] = !query
      ? this.filteredPortNames.slice(0, 20)
      : this.filteredPortNames
        .filter(
          (p) =>
            (p.port_name || '').toLowerCase().includes(query) ||
            (p.country || '').toLowerCase().includes(query),
        )
        .slice(0, 20);
  }

  getMidPortSuggestions(index: number): Array<{ port_name?: string; country?: string }> {
    return this.midPortSuggestions[index] || this.filteredPortNames.slice(0, 20);
  }

  onPortOptionSelected(event: MatAutocompleteSelectedEvent, type: 'start' | 'end'): void {
    const selectedValue = event.option.value;
    if (selectedValue === 'Others') {
      if (type === 'start') {
        this.startIsOthers = true;
        this.voyageForm.patchValue({ startPort: 'Others', startIsOthers: true, startCustomPortName: '' });
        this.startPortSuggestions = [];
        setTimeout(() => {
          const input = document.querySelector<HTMLElement>('[formcontrolname="startCustomPortName"] input, [formcontrolname="startCustomPortName"]');
          input?.focus();
        }, 100);
      } else {
        this.endIsOthers = true;
        this.voyageForm.patchValue({ endPort: 'Others', endIsOthers: true, endCustomPortName: '' });
        this.endPortSuggestions = [];
        setTimeout(() => {
          const input = document.querySelector<HTMLElement>('[formcontrolname="endCustomPortName"] input, [formcontrolname="endCustomPortName"]');
          input?.focus();
        }, 100);
      }
    } else {
      // Auto-fetch lat/long from filteredPortNames
      const match = (this.filteredPortNames || []).find(
        (p) => (p.port_name || '').toLowerCase().trim() === (selectedValue || '').toLowerCase().trim()
      );
      if (match) {
        if (type === 'start') {
          this.voyageForm.patchValue({
            startLat: match.latitude != null ? Math.abs(match.latitude) : (match.lat_deg != null ? Math.abs(match.lat_deg) : null),
            startLatDir: this.resolveLatDir(match.latitude ?? match.lat_deg, match.lat_dir),
            startLong: match.longitude != null ? Math.abs(match.longitude) : (match.lng_deg != null ? Math.abs(match.lng_deg) : null),
            startLongDir: this.resolveLongDir(match.longitude ?? match.lng_deg, match.lng_dir),
            startUnlocode: match.port_code || ''
          });
        } else {
          this.voyageForm.patchValue({
            endLat: match.latitude != null ? Math.abs(match.latitude) : (match.lat_deg != null ? Math.abs(match.lat_deg) : null),
            endLatDir: this.resolveLatDir(match.latitude ?? match.lat_deg, match.lat_dir),
            endLong: match.longitude != null ? Math.abs(match.longitude) : (match.lng_deg != null ? Math.abs(match.lng_deg) : null),
            endLongDir: this.resolveLongDir(match.longitude ?? match.lng_deg, match.lng_dir),
            endUnlocode: match.port_code || ''
          });
        }
      }
    }
  }

  onMidPortOptionSelected(event: MatAutocompleteSelectedEvent, index: number): void {
    const selectedValue = event.option.value;
    if (selectedValue === 'Others') {
      this.selectMidPortOthers(index);
    } else {
      const match = (this.filteredPortNames || []).find(
        (p) => (p.port_name || '').toLowerCase().trim() === (selectedValue || '').toLowerCase().trim()
      );
      if (match && this.itinerary.at(index)) {
        this.itinerary.at(index).patchValue({
          lat: match.latitude != null ? match.latitude : (match.lat_deg != null ? match.lat_deg : null),
          latDir: match.lat_dir || 'N',
          long: match.longitude != null ? match.longitude : (match.lng_deg != null ? match.lng_deg : null),
          longDir: match.lng_dir || 'E',
          unlocode: match.port_code || ''
        });
      }
    }
  }

  onPortBlur(type: 'start' | 'end'): void {
    const currentVal = (type === 'start' ? this.voyageForm.get('startPort')?.value : this.voyageForm.get('endPort')?.value) || '';
    if (!currentVal || currentVal === 'Others') return;

    const match = (this.filteredPortNames || []).find((p: any) => {
      const q = String(currentVal).toLowerCase().replace(/[^a-z0-9]/g, '');
      const n = String(p?.port_name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const c = String(p?.port_code || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      return n === q || (q.length > 3 && (n.startsWith(q) || q.startsWith(n))) || (c && c === q);
    });

    if (match) {
      if (type === 'start') {
        const curLat = this.voyageForm.get('startLat')?.value;
        const curLon = this.voyageForm.get('startLong')?.value;
        if (!curLat && !curLon) {
          this.voyageForm.patchValue({
            startLat: match.latitude != null ? Math.abs(match.latitude) : (match.lat_deg != null ? Math.abs(match.lat_deg) : null),
            startLatDir: this.resolveLatDir(match.latitude ?? match.lat_deg, match.lat_dir),
            startLong: match.longitude != null ? Math.abs(match.longitude) : (match.lng_deg != null ? Math.abs(match.lng_deg) : null),
            startLongDir: this.resolveLongDir(match.longitude ?? match.lng_deg, match.lng_dir),
            startUnlocode: match.port_code || ''
          });
        }
      } else {
        const curLat = this.voyageForm.get('endLat')?.value;
        const curLon = this.voyageForm.get('endLong')?.value;
        if (!curLat && !curLon) {
          this.voyageForm.patchValue({
            endLat: match.latitude != null ? Math.abs(match.latitude) : (match.lat_deg != null ? Math.abs(match.lat_deg) : null),
            endLatDir: this.resolveLatDir(match.latitude ?? match.lat_deg, match.lat_dir),
            endLong: match.longitude != null ? Math.abs(match.longitude) : (match.lng_deg != null ? Math.abs(match.lng_deg) : null),
            endLongDir: this.resolveLongDir(match.longitude ?? match.lng_deg, match.lng_dir),
            endUnlocode: match.port_code || ''
          });
        }
      }
    }
  }

  selectVesselOthers(): void {
    this.vesselIsOthers = true;
    this.voyageForm.patchValue({
      vesselName: 'Others',
      vesselIsOthers: true,
      customVesselName: '',
      imo: '',
      vesselType: ''
    });
    setTimeout(() => {
      const input = document.querySelector<HTMLElement>('[formcontrolname="customVesselName"] input, [formcontrolname="customVesselName"]');
      input?.focus();
    }, 100);
  }

  cancelVesselOthers(): void {
    this.vesselIsOthers = false;
    this.voyageForm.patchValue({
      vesselName: '',
      vesselIsOthers: false,
      customVesselName: '',
      imo: '',
      vesselType: ''
    });
  }

  cancelOthersOption(type: 'start' | 'end'): void {
    if (type === 'start') {
      this.startIsOthers = false;
      this.voyageForm.patchValue({ startPort: '', startIsOthers: false, startCustomPortName: '', startUnlocode: '' });
    } else {
      this.endIsOthers = false;
      this.voyageForm.patchValue({ endPort: '', endIsOthers: false, endCustomPortName: '', endUnlocode: '' });
    }
  }

  selectMidPortOthers(index: number): void {
    const item = this.itinerary.at(index);
    if (item) {
      item.patchValue({ isOthers: true, port: 'Others', customPortName: '', unlocode: '' });
      this.midPortSuggestions[index] = [];
      setTimeout(() => {
        const input = document.querySelector<HTMLElement>(`#mid-stop-${index}-custom-name input`);
        input?.focus();
      }, 100);
    }
  }

  cancelMidPortOthers(index: number): void {
    const item = this.itinerary.at(index);
    if (item) {
      item.patchValue({ isOthers: false, port: '', customPortName: '', unlocode: '', lat: '', long: '' });
    }
  }

  displayPortName = (value: string | { port_name?: string }): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.port_name || '';
  };

  shouldShowOthersOption(type: 'start' | 'end' | number): boolean {
    if (type === 'start') {
      const val = this.voyageForm.get('startPort')?.value;
      const str = typeof val === 'string' ? val : val?.port_name || '';
      return str.trim().length >= 3;
    }
    if (type === 'end') {
      const val = this.voyageForm.get('endPort')?.value;
      const str = typeof val === 'string' ? val : val?.port_name || '';
      return str.trim().length >= 3;
    }
    if (typeof type === 'number') {
      const item = this.itinerary.at(type);
      const val = item?.get('port')?.value;
      const str = typeof val === 'string' ? val : val?.port_name || '';
      return str.trim().length >= 3;
    }
    return false;
  }

  shouldShowOthersVessel(): boolean {
    const val = (this.voyageForm.get('vesselName')?.value || '').trim();
    return val.length >= 3;
  }

  onVesselNameInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value?.trim().toLowerCase();
    if (!value) {
      this.vesselSuggestions = this.lookerVessels.slice(0, 30);
      return;
    }
    this.vesselSuggestions = this.lookerVessels
      .filter((v) =>
        (v.vessel_name || '').toLowerCase().includes(value) ||
        String(v.imo || '').includes(value)
      )
      .slice(0, 30);
  }

  onVesselFocus(): void {
    const value = (this.voyageForm.get('vesselName')?.value || '').trim().toLowerCase();
    if (!value) {
      this.vesselSuggestions = this.lookerVessels.slice(0, 30);
    } else {
      this.vesselSuggestions = this.lookerVessels
        .filter((v) =>
          (v.vessel_name || '').toLowerCase().includes(value) ||
          String(v.imo || '').includes(value)
        )
        .slice(0, 30);
    }
  }

  onVesselSelected(vesselName: string): void {
    if (vesselName === 'Others') {
      this.selectVesselOthers();
      return;
    }
    const match = this.findVesselByName(vesselName);
    if (match?.imo != null && match.imo !== '') {
      this.applyVesselImoAndFetchDetails(String(match.imo));
    }
  }

  private applyVesselImoAndFetchDetails(imo: string): void {
    const normalized = imo.replace(/\D/g, '').trim();
    if (!normalized) {
      return;
    }

    if (this.lastQuickOnboardingImo !== normalized) {
      this.vesselTypeAutoPopulated = false;
      this.voyageForm.patchValue({ vesselType: '' }, { emitEvent: false });
    }

    this.voyageForm.patchValue({ imo: normalized }, { emitEvent: false });
    this.fetchQuickOnboardingByImo(normalized);
    this.fetchLastReportedPort(normalized);
  }

  private fetchLastReportedPort(imo: string): void {
    this.voyageRequestsService
      .getLastPortByImo(imo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.success && res.data?.port) {
            const portData = res.data;
            const currentStart = this.voyageForm.get('startPort')?.value;
            if (!currentStart || currentStart.trim() === '') {
              const patchObj: any = {
                startPort: portData.port,
                startLat: portData.lat ?? null,
                startLatDir: portData.latDir || 'N',
                startLong: portData.long ?? null,
                startLongDir: portData.longDir || 'E'
              };
              if (portData.vesselCondition) {
                patchObj.condition = portData.vesselCondition.toLowerCase().includes('ballast') ? 'Ballast' : 'Laden';
              }
              this.voyageForm.patchValue(patchObj);
              this.lastPortAutoPopulated = true;
              this.lastPortDetails = portData;
              this.toastr.info(
                `Departure port auto-filled to ${portData.port}${portData.vesselCondition ? ' (' + portData.vesselCondition + ')' : ''} from latest arrival report (${portData.reportDate || 'Recent'}).`,
                'Auto-Populated Departure Port',
                { timeOut: 4000 }
              );
            }
          }
        },
        error: (err) => {
          console.warn('Could not fetch last arrival report for IMO:', imo, err);
        }
      });
  }

  private fetchQuickOnboardingByImo(imo: string): void {
    const normalized = imo.replace(/\D/g, '').trim();
    if (!/^\d{7}$/.test(normalized)) {
      return;
    }
    if (this.isLoadingVesselDetails) {
      return;
    }

    this.isLoadingVesselDetails = true;
    this.portRequestFormService
      .quickOnboarding(normalized)
      .pipe(
        finalize(() => (this.isLoadingVesselDetails = false)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          this.lastQuickOnboardingImo = normalized;
          this.applyQuickOnboardingResponse(res);
        },
        error: () => {
          this.vesselTypeAutoPopulated = false;
          this.toastr.warning(
            'Could not load vessel type for the selected IMO.',
          );
        },
      });
  }

  private applyQuickOnboardingResponse(res: any): void {
    const data = res?.data ?? res;
    if (!data || (typeof data === 'object' && !Object.keys(data).length)) {
      this.vesselTypeAutoPopulated = false;
      this.toastr.warning('No vessel details returned for this IMO.');
      return;
    }

    const typeOfVessel = String(
      data.type_of_vessel ?? data.vessel_type ?? data.vesselType ?? '',
    ).trim();

    const patch: Record<string, string | number> = {};

    if (typeOfVessel) {
      patch['vesselType'] = typeOfVessel;
    }
    if (data.year_built != null && data.year_built !== '') {
      patch['yearBuilt'] = data.year_built;
    }
    if (data.deadweight != null && data.deadweight !== '') {
      patch['deadWeight'] = data.deadweight;
    }
    if (data.gross_tonnage != null && data.gross_tonnage !== '') {
      patch['grossTonnage'] = data.gross_tonnage;
    }

    const draftVal = data.draught ?? data.summer_draught ?? data.draft ?? data.design_draught ?? data.scantling_draught ?? '';
    if (draftVal != null && draftVal !== '') {
      if (!this.voyageForm.get('aftDraft')?.value) patch['aftDraft'] = draftVal;
      if (!this.voyageForm.get('forwardDraft')?.value) patch['forwardDraft'] = draftVal;
      if (!this.voyageForm.get('beta')?.value) patch['beta'] = draftVal;
    }
    const airDraftVal = data.air_draught ?? data.airDraft ?? data.air_draft ?? '';
    if (airDraftVal != null && airDraftVal !== '') {
      patch['airDraft'] = airDraftVal;
    }

    if (Object.keys(patch).length > 0) {
      this.voyageForm.patchValue(patch, { emitEvent: false });
      this.vesselTypeAutoPopulated = true;
      this.toastr.success(
        `Vessel details auto-populated for IMO: ${this.lastQuickOnboardingImo}`,
      );
    }
  }

  private resetVesselAutoFields(): void {
    this.vesselTypeAutoPopulated = false;
    this.lastQuickOnboardingImo = null;
    this.voyageForm.patchValue(
      { imo: '', vesselType: '', yearBuilt: '' },
      { emitEvent: false },
    );
  }

  private findVesselByName(
    name: string | null | undefined,
  ): { vessel_name?: string; imo?: string | number } | undefined {
    if (!name) {
      return undefined;
    }
    const normalized = name.trim().toLowerCase();
    return this.lookerVessels.find(
      (v) => (v.vessel_name || '').trim().toLowerCase() === normalized,
    );
  }

  /* ================= ITINERARY WAYPOINTS & ROUTE REORDERING ================= */

  get itinerary(): FormArray {
    return this.voyageForm.get('itinerary') as FormArray;
  }


  resolveLatDir(lat: any, dir?: any): string {
    const s = String(dir ?? '').trim().toUpperCase();
    if (s === 'N' || s === 'S') return s;
    if (lat != null && lat !== '') {
      const num = Number(lat);
      if (!isNaN(num)) {
        return num < 0 ? 'S' : 'N';
      }
    }
    return 'N';
  }

  resolveLongDir(lon: any, dir?: any): string {
    const s = String(dir ?? '').trim().toUpperCase();
    if (s === 'E' || s === 'W') return s;
    if (lon != null && lon !== '') {
      const num = Number(lon);
      if (!isNaN(num)) {
        return num < 0 ? 'W' : 'E';
      }
    }
    return 'E';
  }

  createItinerary(data?: any): FormGroup {
    let etaVal: any = data?.eta || '';
    if (etaVal && typeof etaVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(etaVal)) {
      const parts = etaVal.split('T')[0].split('-');
      if (parts.length === 3) {
        etaVal = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }

    let cancelDateVal: any = data?.cancelDate || '';
    if (cancelDateVal && typeof cancelDateVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(cancelDateVal)) {
      const parts = cancelDateVal.split('T')[0].split('-');
      if (parts.length === 3) {
        cancelDateVal = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
    }

    const itinLat = data?.lat ?? '';
    const itinLong = data?.long ?? data?.lon ?? '';
    const itinLatDir = this.resolveLatDir(itinLat, data?.latDir);
    const itinLongDir = this.resolveLongDir(itinLong, data?.longDir);

    return this.fb.group({
      port: [data?.port || ''],
      isOthers: [data?.isOthers || false],
      customPortName: [data?.customPortName || ''],
      unlocode: [data?.unlocode || ''],
      lat: [itinLat !== '' && itinLat != null ? Math.abs(itinLat) : ''],
      latDir: [itinLatDir],
      long: [itinLong !== '' && itinLong != null ? Math.abs(itinLong) : ''],
      longDir: [itinLongDir],
      priority: [data?.priority || 'Normal'],
      operation: [data?.operation || 'Discharging', Validators.required],
      portStay: [data?.portStay ?? '', [Validators.min(0), Validators.max(168)]],
      eta: [etaVal],
      etaTime: [data?.etaTime || ''],
      cancelDate: [cancelDateVal],
    });
  }

  addItinerary(): void {
    this.itinerary.push(this.createItinerary());
    this.itinerary.updateValueAndValidity();
  }

  removeItinerary(index: number): void {
    this.itinerary.removeAt(index);
    this.itinerary.updateValueAndValidity();
  }

  dropRouteStop(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    const destIndex = this.itinerary.length;

    if (event.previousIndex === destIndex) {
      const targetMidIndex = event.currentIndex;
      const targetMidGroup = this.itinerary.at(targetMidIndex);
      const targetMidData = targetMidGroup.value;

      const currentDestData = {
        port: this.voyageForm.get('endPort')?.value,
        isOthers: this.endIsOthers,
        customPortName: this.voyageForm.get('endCustomPortName')?.value,
        unlocode: this.voyageForm.get('endUnlocode')?.value,
        lat: this.voyageForm.get('endLat')?.value,
        latDir: this.voyageForm.get('endLatDir')?.value || 'N',
        long: this.voyageForm.get('endLong')?.value,
        longDir: this.voyageForm.get('endLongDir')?.value || 'E',
        priority: 'Normal',
        operation: 'Discharging',
        portStay: '',
        eta: '',
        cancelDate: ''
      };

      this.endIsOthers = targetMidData.isOthers;
      this.voyageForm.patchValue({
        endPort: targetMidData.port,
        endIsOthers: targetMidData.isOthers,
        endCustomPortName: targetMidData.customPortName,
        endUnlocode: targetMidData.unlocode,
        endLat: targetMidData.lat,
        endLatDir: targetMidData.latDir,
        endLong: targetMidData.long,
        endLongDir: targetMidData.longDir
      });

      this.itinerary.removeAt(targetMidIndex);
      const newMidGroup = this.createItinerary(currentDestData);
      this.itinerary.insert(targetMidIndex, newMidGroup);

      this.triggerReorderAnimation(destIndex, targetMidIndex);
      return;
    }

    if (event.currentIndex === destIndex) {
      const srcMidIndex = event.previousIndex;
      const srcMidGroup = this.itinerary.at(srcMidIndex);
      const srcMidData = srcMidGroup.value;

      const currentDestData = {
        port: this.voyageForm.get('endPort')?.value,
        isOthers: this.endIsOthers,
        customPortName: this.voyageForm.get('endCustomPortName')?.value,
        unlocode: this.voyageForm.get('endUnlocode')?.value,
        lat: this.voyageForm.get('endLat')?.value,
        latDir: this.voyageForm.get('endLatDir')?.value || 'N',
        long: this.voyageForm.get('endLong')?.value,
        longDir: this.voyageForm.get('endLongDir')?.value || 'E',
        priority: 'Normal',
        operation: 'Discharging',
        portStay: '',
        eta: '',
        cancelDate: ''
      };

      this.endIsOthers = srcMidData.isOthers;
      this.voyageForm.patchValue({
        endPort: srcMidData.port,
        endIsOthers: srcMidData.isOthers,
        endCustomPortName: srcMidData.customPortName,
        endUnlocode: srcMidData.unlocode,
        endLat: srcMidData.lat,
        endLatDir: srcMidData.latDir,
        endLong: srcMidData.long,
        endLongDir: srcMidData.longDir
      });

      this.itinerary.removeAt(srcMidIndex);
      const newMidGroup = this.createItinerary(currentDestData);
      this.itinerary.push(newMidGroup);

      this.triggerReorderAnimation(srcMidIndex, destIndex);
      return;
    }

    // Standard waypoint drag and drop reordering
    const formArray = this.itinerary;
    const currentControl = formArray.at(event.previousIndex);
    formArray.removeAt(event.previousIndex);
    formArray.insert(event.currentIndex, currentControl);
    this.triggerReorderAnimation(event.previousIndex, event.currentIndex);
  }

  moveStopUp(index: number): void {
    if (index <= 0) return;
    this.dropRouteStop({ previousIndex: index, currentIndex: index - 1 } as CdkDragDrop<any[]>);
  }

  moveStopDown(index: number): void {
    const destIndex = this.itinerary.length;
    if (index >= destIndex) return;
    this.dropRouteStop({ previousIndex: index, currentIndex: index + 1 } as CdkDragDrop<any[]>);
  }

  isMoveUpDisabled(index: number): boolean {
    return index === 0;
  }

  isMoveDownDisabled(index: number): boolean {
    return index === this.itinerary.length;
  }

  private triggerReorderAnimation(from: number, to: number): void {
    if (from > to) {
      this.movedUpIndex = to;
    } else {
      this.movedDownIndex = to;
    }
    setTimeout(() => {
      this.movedUpIndex = null;
      this.movedDownIndex = null;
    }, 450);
  }

  /* ================= SPEED SETS ================= */

  get speedSets(): FormArray {
    return this.voyageForm.get('speedSets') as FormArray;
  }

  createSpeedSet(data?: any): FormGroup {
    return this.fb.group({
      speed: [data?.speed || 12.5, [Validators.required, Validators.min(0)]],
      consumption: [data?.consumption || 25, [Validators.required, Validators.min(0)]]
    });
  }

  addSpeedSet(): void {
    this.speedSets.push(this.createSpeedSet());
  }

  removeSet(index: number): void {
    if (this.speedSets.length > 1) {
      this.speedSets.removeAt(index);
    } else {
      this.toastr.warning('At least one speed and consumption set is required');
    }
  }

  /* ================= VALIDATION HELPERS ================= */

  isStartInvalid(): boolean {
    const isOthers = this.startIsOthers || String(this.voyageForm.get('startPort')?.value ?? '').toLowerCase() === 'others';
    const lat = String(this.voyageForm.get('startLat')?.value ?? '').trim();
    const long = String(this.voyageForm.get('startLong')?.value ?? '').trim();
    const unlocode = String(this.voyageForm.get('startUnlocode')?.value ?? '').trim();
    const hasCoords = lat !== '' && long !== '';

    if (isOthers) {
      const customName = String(this.voyageForm.get('startCustomPortName')?.value ?? '').trim();
      return !(customName && (hasCoords || unlocode));
    }

    const port = String(this.voyageForm.get('startPort')?.value ?? '').trim();
    return !(port || hasCoords || unlocode);
  }

  isEndInvalid(): boolean {
    const isOthers = this.endIsOthers || String(this.voyageForm.get('endPort')?.value ?? '').toLowerCase() === 'others';
    const lat = String(this.voyageForm.get('endLat')?.value ?? '').trim();
    const long = String(this.voyageForm.get('endLong')?.value ?? '').trim();
    const unlocode = String(this.voyageForm.get('endUnlocode')?.value ?? '').trim();
    const hasCoords = lat !== '' && long !== '';

    if (isOthers) {
      const customName = String(this.voyageForm.get('endCustomPortName')?.value ?? '').trim();
      return !(customName && (hasCoords || unlocode));
    }

    const port = String(this.voyageForm.get('endPort')?.value ?? '').trim();
    return !(port || hasCoords || unlocode);
  }

  isStartErrorVisible(): boolean {
    const startTouched = !!(
      this.voyageForm.get('startPort')?.touched ||
      this.voyageForm.get('startCustomPortName')?.touched ||
      this.voyageForm.get('startUnlocode')?.touched ||
      this.voyageForm.get('startLat')?.touched ||
      this.voyageForm.get('startLong')?.touched
    );
    return this.isStartInvalid() && (startTouched || this.formSubmitted);
  }

  isEndErrorVisible(): boolean {
    const endTouched = !!(
      this.voyageForm.get('endPort')?.touched ||
      this.voyageForm.get('endCustomPortName')?.touched ||
      this.voyageForm.get('endUnlocode')?.touched ||
      this.voyageForm.get('endLat')?.touched ||
      this.voyageForm.get('endLong')?.touched
    );
    return this.isEndInvalid() && (endTouched || this.formSubmitted);
  }

  validateRouteEndpoints = (group: AbstractControl): ValidationErrors | null => {
    const form = group as FormGroup;
    const errors: ValidationErrors = {};

    const startPort = String(form.get('startPort')?.value ?? '').trim();
    const startIsOthers = form.get('startIsOthers')?.value || startPort.toLowerCase() === 'others';
    const startLat = String(form.get('startLat')?.value ?? '').trim();
    const startLong = String(form.get('startLong')?.value ?? '').trim();
    const startUnlocode = String(form.get('startUnlocode')?.value ?? '').trim();
    const startHasCoords = startLat !== '' && startLong !== '';

    if (startIsOthers) {
      const customName = String(form.get('startCustomPortName')?.value ?? '').trim();
      if (!(customName && (startHasCoords || startUnlocode))) {
        errors['startRequired'] = true;
      }
    } else {
      if (!(startPort || startHasCoords || startUnlocode)) {
        errors['startRequired'] = true;
      }
    }

    const endPort = String(form.get('endPort')?.value ?? '').trim();
    const endIsOthers = form.get('endIsOthers')?.value || endPort.toLowerCase() === 'others';
    const endLat = String(form.get('endLat')?.value ?? '').trim();
    const endLong = String(form.get('endLong')?.value ?? '').trim();
    const endUnlocode = String(form.get('endUnlocode')?.value ?? '').trim();
    const endHasCoords = endLat !== '' && endLong !== '';

    if (endIsOthers) {
      const customName = String(form.get('endCustomPortName')?.value ?? '').trim();
      if (!(customName && (endHasCoords || endUnlocode))) {
        errors['endRequired'] = true;
      }
    } else {
      if (!(endPort || endHasCoords || endUnlocode)) {
        errors['endRequired'] = true;
      }
    }

    return Object.keys(errors).length ? errors : null;
  };

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }

  goToList(): void {
    this.router.navigate(['/routing-request-list']);
  }

  formatIsoDateString(val: any): string {
    if (!val) return '';
    if (val instanceof Date) {
      const year = val.getFullYear();
      const month = String(val.getMonth() + 1).padStart(2, '0');
      const day = String(val.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    if (typeof val === 'string') {
      return val.split('T')[0];
    }
    return String(val);
  }

  isSavingDraft = false;
  // Route CSV / Waypoints File Upload
  uploadedCsvFile: File | null = null;
  uploadedCsvFileName: string = '';
  uploadedCsvFileSize: string = '';
  uploadedCsvData: string = '';
  uploadedCsvRowCount: number = 0;
  isDraggingCsvOver = false;

  isSubmittingForm = false;

  saveAsDraft(): void {
    if (this.isSavingDraft || this.isSubmittingForm) return;
    this.isSavingDraft = true;

    const formData = this.voyageForm.getRawValue();

    if (this.vesselIsOthers && formData.customVesselName) {
      formData.vesselName = formData.customVesselName;
    }
    if (this.startIsOthers && formData.startCustomPortName) {
      formData.startPort = formData.startCustomPortName;
    }
    if (this.endIsOthers && formData.endCustomPortName) {
      formData.endPort = formData.endCustomPortName;
    }

    if (formData.itinerary && Array.isArray(formData.itinerary)) {
      formData.itinerary.forEach((item: any) => {
        if (item.isOthers && item.customPortName) {
          item.port = item.customPortName;
        }
        item.eta = this.formatIsoDateString(item.eta);
        item.cancelDate = this.formatIsoDateString(item.cancelDate);
      });
    }

    formData.departureDate = this.formatIsoDateString(formData.departureDate);

    const payload = {
      ...formData,
      status: 'Draft',
      isDraft: true,
      clientName: localStorage.getItem('username') || 'Meraki Operations',
      vesselIsOthers: this.vesselIsOthers,
      startIsOthers: this.startIsOthers,
      startCustomPortName: formData.startCustomPortName || '',
      startUnlocode: formData.startUnlocode || '',
      endIsOthers: this.endIsOthers,
      endCustomPortName: formData.endCustomPortName || '',
      endUnlocode: formData.endUnlocode || '',
      routeCsvName: this.uploadedCsvFileName || '',
      routeCsvSize: this.uploadedCsvFileSize || '',
      routeCsvData: this.uploadedCsvData || '',
      routeCsvRowCount: this.uploadedCsvRowCount || 0
    };

    if (this.isEditMode && this.editingRequestId) {
      this.voyageRequestsService.updateRequest(this.editingRequestId, payload).subscribe({
        next: (res) => {
          this.isSavingDraft = false;
          this.toastr.success(`Draft ${this.editingRequestId} updated successfully!`, 'Draft Saved');
          this.goToList();
        },
        error: (err) => {
          this.isSavingDraft = false;
          this.toastr.error('Failed to update draft', 'Error');
        }
      });
    } else {
      this.voyageRequestsService.createRequest(payload).subscribe({
        next: (res) => {
          this.isSavingDraft = false;
          const reqId = res?.data?.requestId || 'Draft';
          this.toastr.success(`Routing request draft ${reqId} saved successfully!`, 'Draft Saved');
          this.goToList();
        },
        error: (err) => {
          this.isSavingDraft = false;
          this.toastr.error('Failed to save draft', 'Error');
        }
      });
    }
  }

  // ================= CSV DRAG & DROP HANDLERS (GLOBAL & LOCAL) =================
  private dragTargetCounter = 0;

  @HostListener('window:dragover', ['$event'])
  onWindowDragOver(event: DragEvent): void {
    if (!this.canEditForm) return;
    const types = event.dataTransfer?.types;
    if (types && Array.from(types).includes('Files')) {
      event.preventDefault();
      this.isDraggingCsvOver = true;
    }
  }

  @HostListener('window:dragenter', ['$event'])
  onWindowDragEnter(event: DragEvent): void {
    if (!this.canEditForm) return;
    const types = event.dataTransfer?.types;
    if (types && Array.from(types).includes('Files')) {
      this.dragTargetCounter++;
      this.isDraggingCsvOver = true;
    }
  }

  @HostListener('window:dragleave', ['$event'])
  onWindowDragLeave(event: DragEvent): void {
    if (!this.canEditForm) return;
    this.dragTargetCounter = Math.max(0, this.dragTargetCounter - 1);
    if (this.dragTargetCounter === 0) {
      this.isDraggingCsvOver = false;
    }
  }

  @HostListener('window:drop', ['$event'])
  onWindowDrop(event: DragEvent): void {
    if (!this.canEditForm) return;
    const types = event.dataTransfer?.types;
    if (types && Array.from(types).includes('Files')) {
      event.preventDefault();
      event.stopPropagation();
      this.dragTargetCounter = 0;
      this.isDraggingCsvOver = false;
      if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0];
        this.handleSelectedCsvFile(file);
      }
    }
  }

  onCsvFileDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragTargetCounter = 0;
    this.isDraggingCsvOver = false;
    if (!this.canEditForm) return;

    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      this.handleSelectedCsvFile(file);
    }
  }

  onCsvDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.canEditForm) {
      this.isDraggingCsvOver = true;
    }
  }

  onCsvDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onCsvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.handleSelectedCsvFile(file);
    }
  }

  // Route CSV Map Preview State
  parsedCsvWaypoints: Array<{ name: string; lat: number; lon: number; speed?: string }> = [];
  isCsvMapExpanded: boolean = true;
  private csvPreviewMap: L.Map | null = null;
  private csvPolyline: L.Polyline | null = null;
  private csvMarkers: L.Layer[] = [];

  handleSelectedCsvFile(file: File): void {
    const maxSizeBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      this.toastr.error('File size exceeds 10MB limit. Please upload a smaller CSV file.', 'File Too Large');
      return;
    }

    const validExts = ['.csv', '.txt', '.xlsx', '.xls'];
    const fileNameLower = file.name.toLowerCase();
    const hasValidExt = validExts.some(ext => fileNameLower.endsWith(ext));
    if (!hasValidExt) {
      this.toastr.warning('Please upload a valid CSV, TXT, or Excel waypoint file.', 'Invalid File Format');
      return;
    }

    this.uploadedCsvFile = file;
    this.uploadedCsvFileName = file.name;
    this.uploadedCsvFileSize = this.formatFileSize(file.size);

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const content = e.target.result as string;
      this.uploadedCsvData = content;
      if (typeof content === 'string') {
        const lines = content.split(/\r\n|\n/).filter(line => line.trim().length > 0);
        this.uploadedCsvRowCount = Math.max(0, lines.length - 1);
        this.parseCsvAndRenderMap(content);
      }
      this.toastr.success(`Attached "${file.name}" (${this.uploadedCsvFileSize})`, 'Route File Attached');
    };
    reader.readAsText(file);
  }

  parseCsvAndRenderMap(content: string): void {
    const lines = content.split(/\r\n|\n/).map(l => l.trim()).filter(l => l.length > 0);
    if (lines.length < 2) {
      this.parsedCsvWaypoints = [];
      this.destroyCsvMap();
      return;
    }

    // Identify header indices (flexible search for lat/long/name/speed columns)
    const headerCols = lines[0].split(',').map(h => h.trim().toLowerCase());
    let latIdx = headerCols.findIndex(h => h.includes('lat'));
    let lonIdx = headerCols.findIndex(h => h.includes('long') || h.includes('lng') || h.includes('lon'));
    let nameIdx = headerCols.findIndex(h => h.includes('wpt name') || h.includes('waypoint') || h.includes('name'));
    let numIdx = headerCols.findIndex(h => h.includes('num') || h.includes('no') || h.includes('wpt'));
    let spdIdx = headerCols.findIndex(h => h.includes('spd') || h.includes('speed'));

    // Fallback index defaults if standard header not matched
    if (latIdx === -1) latIdx = 2;
    if (lonIdx === -1) lonIdx = 3;

    const waypoints: Array<{ name: string; lat: number; lon: number; speed?: string }> = [];

    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split(',').map(p => p.trim());
      if (parts.length <= Math.max(latIdx, lonIdx)) continue;

      const lat = parseFloat(parts[latIdx]);
      const lon = parseFloat(parts[lonIdx]);

      if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        const name = (nameIdx !== -1 && parts[nameIdx]) 
          ? parts[nameIdx] 
          : ((numIdx !== -1 && parts[numIdx]) ? `WPT ${parts[numIdx]}` : `WPT ${i}`);
        const speed = (spdIdx !== -1 && parts[spdIdx]) ? parts[spdIdx] : undefined;

        waypoints.push({ name, lat, lon, speed });
      }
    }

    this.parsedCsvWaypoints = waypoints;

    if (waypoints.length > 0) {
      setTimeout(() => {
        this.initOrUpdateCsvMap();
      }, 100);
    } else {
      this.destroyCsvMap();
    }
  }

  toggleCsvMap(): void {
    this.isCsvMapExpanded = !this.isCsvMapExpanded;
    if (this.isCsvMapExpanded && this.parsedCsvWaypoints.length > 0) {
      setTimeout(() => {
        if (this.csvPreviewMap) {
          this.csvPreviewMap.invalidateSize();
          const latLngs: L.LatLngExpression[] = this.parsedCsvWaypoints.map(w => [w.lat, w.lon]);
          if (latLngs.length > 0) {
            const bounds = L.latLngBounds(latLngs);
            this.csvPreviewMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
          }
        } else {
          this.initOrUpdateCsvMap();
        }
      }, 50);
    }
  }

  private initOrUpdateCsvMap(): void {
    const mapEl = document.getElementById('csv-route-preview-map');
    if (!mapEl) return;

    if (!this.csvPreviewMap) {
      this.csvPreviewMap = L.map('csv-route-preview-map', {
        attributionControl: false,
        zoomControl: true
      });

      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
        {
          maxNativeZoom: 16,
          maxZoom: 19
        }
      ).addTo(this.csvPreviewMap);
      L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
        {
          maxNativeZoom: 16,
          maxZoom: 19
        }
      ).addTo(this.csvPreviewMap);
    }

    // Clear previous layers
    if (this.csvPolyline) {
      this.csvPolyline.remove();
      this.csvPolyline = null;
    }
    this.csvMarkers.forEach(m => m.remove());
    this.csvMarkers = [];

    const latLngs: L.LatLngExpression[] = this.parsedCsvWaypoints.map(w => [w.lat, w.lon]);

    // Draw route polyline
    this.csvPolyline = L.polyline(latLngs, {
      color: '#3b82f6',
      weight: 4,
      opacity: 0.9,
      dashArray: '8, 6',
      lineCap: 'round',
      lineJoin: 'round'
    }).addTo(this.csvPreviewMap);

    // Draw markers
    this.parsedCsvWaypoints.forEach((wp, idx) => {
      const isStart = idx === 0;
      const isEnd = idx === this.parsedCsvWaypoints.length - 1;

      let markerHtml = '';
      if (isStart) {
        markerHtml = `<div class="csv-map-marker start-marker"><span class="marker-dot"></span><span class="marker-label">START</span></div>`;
      } else if (isEnd) {
        markerHtml = `<div class="csv-map-marker end-marker"><span class="marker-dot"></span><span class="marker-label">DEST</span></div>`;
      } else {
        markerHtml = `<div class="csv-map-marker wp-marker"><span class="marker-dot"></span></div>`;
      }

      const icon = L.divIcon({
        className: 'custom-csv-leaflet-marker',
        html: markerHtml,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
      });

      const marker = L.marker([wp.lat, wp.lon], { icon }).addTo(this.csvPreviewMap!);
      
      const popupContent = `
        <div style="font-family: sans-serif; font-size: 12px; color: #0f172a; min-width: 130px;">
          <strong style="font-size: 13px; color: #1e40af;">#${idx + 1} ${wp.name || 'Waypoint'}</strong>
          <div style="margin-top: 4px; color: #475569;">Lat: ${wp.lat.toFixed(4)}°</div>
          <div style="color: #475569;">Lon: ${wp.lon.toFixed(4)}°</div>
          ${wp.speed ? `<div style="margin-top: 2px; color: #0284c7; font-weight: 600;">Spd: ${wp.speed} kn</div>` : ''}
        </div>
      `;
      marker.bindPopup(popupContent);
      this.csvMarkers.push(marker);
    });

    if (latLngs.length > 0) {
      const bounds = L.latLngBounds(latLngs);
      this.csvPreviewMap.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
      this.csvPreviewMap.invalidateSize();
    }
  }

  private destroyCsvMap(): void {
    if (this.csvPreviewMap) {
      this.csvPreviewMap.remove();
      this.csvPreviewMap = null;
    }
    this.csvPolyline = null;
    this.csvMarkers = [];
  }

  removeCsvFile(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.canEditForm) return;
    this.uploadedCsvFile = null;
    this.uploadedCsvFileName = '';
    this.uploadedCsvFileSize = '';
    this.uploadedCsvData = '';
    this.uploadedCsvRowCount = 0;
    this.parsedCsvWaypoints = [];
    this.destroyCsvMap();
    this.toastr.info('Route CSV attachment removed', 'File Removed');
  }

  downloadCurrentCsv(event?: Event): void {
    if (event) event.stopPropagation();
    if (!this.uploadedCsvData && !this.uploadedCsvFileName) return;
    
    const blob = new Blob([this.uploadedCsvData], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.uploadedCsvFileName || 'voyage_route_waypoints.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  downloadDemoCsv(event?: Event): void {
    if (event) event.stopPropagation();
    
    const sampleCsvContent = 
`Route Name/WPT Num,WPT Name,WPT Latitude,WPT Longtitude,From,To,XTE(NM),PlnSpd(kn),ROT(Deg/Min),Radius(NM),Type
1,PORT SAID,31.264166,32.316666,,,0.5,14.0,15,0.85,Rhumb
2,GIBRALTAR STRAIT,36.083500,-5.026200,,,0.5,14.0,15,0.85,Rhumb
3,ROTTERDAM PILOT,51.980000,3.950000,,,0.5,14.0,15,0.85,Rhumb`;

    const blob = new Blob([sampleCsvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_route_waypoints_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    this.toastr.info('Sample waypoint template downloaded', 'Template Downloaded');
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  onSubmit(): void {
    this.formSubmitted = true;
    this.voyageForm.markAllAsTouched();

    if (this.voyageForm.invalid) {
      this.scrollToFirstError();
      return;
    }

    const formData = this.voyageForm.getRawValue();

    if (this.vesselIsOthers && formData.customVesselName) {
      formData.vesselName = formData.customVesselName;
    }
    if (this.startIsOthers && formData.startCustomPortName) {
      formData.startPort = formData.startCustomPortName;
    }
    if (this.endIsOthers && formData.endCustomPortName) {
      formData.endPort = formData.endCustomPortName;
    }
    if (formData.itinerary && Array.isArray(formData.itinerary)) {
      formData.itinerary.forEach((item: any) => {
        if (item.isOthers && item.customPortName) {
          item.port = item.customPortName;
        }
        item.eta = this.formatIsoDateString(item.eta);
        item.cancelDate = this.formatIsoDateString(item.cancelDate);
      });
    }

    formData.departureDate = this.formatIsoDateString(formData.departureDate);

    const vessel = (formData.vesselName || '').trim().toUpperCase();
    const isNewVessel = !this.vesselIsOthers && vessel && !this.onboardedVessels.has(vessel);

    if (isNewVessel && !this.isEditMode) {
      const dialogRef = this.dialog.open(VesselOnboardEmailComponent, {
        width: '420px',
        data: { vessel }
      });

      dialogRef
        .afterClosed()
        .pipe(takeUntil(this.destroy$))
        .subscribe((result) => {
          if (result) {
            this.onboardedVessels.add(vessel);
            this.saveAndFinalizeRequest(formData);
          }
        });
    } else {
      this.saveAndFinalizeRequest(formData);
    }
  }

  private saveAndFinalizeRequest(formData: any): void {
    if (this.isSavingDraft || this.isSubmittingForm) return;
    this.isSubmittingForm = true;

    const isSubmittingDraft = this.isEditMode && this.editingItemStatus === 'Draft';
    const isResubmittingEdit = this.isEditMode && this.editingItemStatus === 'Edit Requested';

    let action: string | undefined = undefined;
    let newStatus = this.isEditMode ? this.editingItemStatus : 'Requested';

    if (isSubmittingDraft) {
      action = 'SUBMIT_DRAFT';
      newStatus = 'Requested';
    } else if (isResubmittingEdit) {
      action = 'RESUBMIT_EDIT';
      newStatus = 'Resubmitted';
    }

    const payload = {
      ...formData,
      status: newStatus,
      action: action,
      remarks: isSubmittingDraft ? undefined : this.merakiEditRemarks,
      clientName: localStorage.getItem('username') || 'Meraki Operations',
      vesselIsOthers: this.vesselIsOthers,
      startIsOthers: this.startIsOthers,
      startCustomPortName: formData.startCustomPortName || '',
      startUnlocode: formData.startUnlocode || '',
      endIsOthers: this.endIsOthers,
      endCustomPortName: formData.endCustomPortName || '',
      endUnlocode: formData.endUnlocode || '',
      routeCsvName: this.uploadedCsvFileName || '',
      routeCsvSize: this.uploadedCsvFileSize || '',
      routeCsvData: this.uploadedCsvData || '',
      routeCsvRowCount: this.uploadedCsvRowCount || 0
    };

    if (this.isEditMode && this.editingRequestId) {
      this.voyageRequestsService.updateRequest(this.editingRequestId, payload).subscribe({
        next: (res) => {
          this.isSubmittingForm = false;
          const reqId = res?.data?.id || this.editingRequestId;
          const successMsg = isResubmittingEdit
            ? `Revised Routing Request ${reqId} resubmitted to Meraki Operations!`
            : (isSubmittingDraft
                ? `Draft Routing Request ${reqId} completed and submitted!`
                : `Routing Request ${reqId} updated successfully with audit trail!`);
          const successTitle = isResubmittingEdit ? 'Request Resubmitted' : (isSubmittingDraft ? 'Draft Submitted' : 'Changes Saved');
          this.toastr.success(successMsg, successTitle);
          this.goToList();
        },
        error: (err) => {
          this.isSubmittingForm = false;
          this.toastr.error('Failed to save changes to backend.', 'Save Error');
        }
      });
    } else {
      this.voyageRequestsService.createRequest(payload).subscribe({
        next: (res) => {
          this.isSubmittingForm = false;
          const reqId = res?.data?.requestId || 'Success';
          this.toastr.success(
            `Voyage Routing Request submitted successfully! (ID: ${reqId})`,
            'Request Queued'
          );
          this.resetForm();
          this.goToList();
        },
        error: (err) => {
          this.isSubmittingForm = false;
          this.toastr.error('Failed to submit routing request.', 'Submission Error');
        }
      });
    }
  }

  private scrollToFirstError(): void {
    if (this.voyageForm.get('vesselName')?.invalid) {
      this.focusAndScroll('[formcontrolname="vesselName"]');
      return;
    }
    if (this.voyageForm.get('vesselType')?.invalid) {
      this.focusAndScroll('[formcontrolname="vesselType"]');
      return;
    }
    if (this.voyageForm.get('vesselEmail')?.invalid) {
      this.focusAndScroll('[formcontrolname="vesselEmail"]');
      return;
    }
    if (this.voyageForm.get('departureDate')?.invalid) {
      this.focusAndScroll('.date-picker-field');
      return;
    }
    if (this.voyageForm.get('departureTime')?.invalid) {
      this.focusAndScroll('[formcontrolname="departureTime"]');
      return;
    }
    if (this.isStartErrorVisible()) {
      this.focusAndScroll('.departure-card');
      return;
    }
    if (this.isEndErrorVisible()) {
      this.focusAndScroll('.destination-card');
      return;
    }
    if (this.voyageForm.get('dailyRate')?.invalid) {
      this.openAccordionAndFocus('panelFinancialsExpanded', '[formcontrolname="dailyRate"]');
      return;
    }
  }

  private focusAndScroll(selector: string): void {
    const el = document.querySelector<HTMLElement>(selector);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  }

  private openAccordionAndFocus(panelProp: 'panelSpeedExpanded' | 'panelFinancialsExpanded' | 'panelSafetyExpanded' | 'panelWeatherExpanded', selector: string): void {
    this[panelProp] = true;
    setTimeout(() => {
      this.focusAndScroll(selector);
    }, 250);
  }

  onFixedEtaFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadedFixedEtaFile = input.files[0];
      this.toastr.success(`File '${this.uploadedFixedEtaFile.name}' attached successfully!`);
    }
  }

  resetForm(): void {
    this.formSubmitted = false;
    this.voyageForm.reset({
      consumptionToggle: true,
      optimizationType: 'Instructed Speed',
      speed: 10,
      departureTime: '',
      avoidIce: true,
      avoidHRA: false,
      secaFuel: false,
      tct: false,
      underKeel: 1,
      airDraft: 2,
      maxWind: 40,
      maxWave: 6,
      weatherRestriction: true,
      startLatDir: 'N',
      startLongDir: 'E',
      endLatDir: 'N',
      endLongDir: 'E',
    });
    this.itinerary.clear();
    this.speedSets.clear();
    this.speedSets.push(this.createSpeedSet());
    this.vesselIsOthers = false;
    this.startIsOthers = false;
    this.endIsOthers = false;
  }
}